import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContractTemplate, Instalment } from '../../../hooks/useContractTemplate';
import Card from '../../../components/Card/Card';
import ListContainer from '../../../components/ListContainer/ListContainer';
import ListElement from '../../../components/ListElement/ListElement';
import ElementActions from '../../../components/ElementActions/ElementActions';
import { Icon } from '../../../components/Icon/Icon';
import IconButton from '../../../components/IconButton/IconButton';
import InputNumber from '../../../components/InputNumber/InputNumber';
import SelectInput from '../../../components/SelectInput/SelectInput';
import TextArea from '../../../components/TextArea/TextArea';
import PrimaryButton from '../../../components/PrimaryButton/PrimaryButton';
import Subtitle from '../../../components/Subtitle/Subtitle';
import TinyText from '../../../components/TinyText/TinyText';
import { PaymentMethodType, paymentMethodTypeToTranslationKey } from '../../../shared/paymentMethodToTranslationKey';
import { isValidText } from '../../../shared/validation';
import deleteIcon from '../../../assets/images/bin.svg';

type Props = {
  contractTemplate: ContractTemplate;
  updateContractTemplate: (value: any) => void;
};

export default function PaymentMethodSection({ contractTemplate, updateContractTemplate }: Props) {
  const { t } = useTranslation();
  const [percentage, setPercentage] = useState<number>(100);
  const [method, setMethod] = useState<PaymentMethodType>("CARD");

  const handleRemoveInstalment = (indexToRemove: number) => {
    const updatedInstalments = contractTemplate.payment.instalments.filter((_, index) => index !== indexToRemove);
    updateContractTemplate({
      payment: {
        ...contractTemplate.payment,
        instalments: updatedInstalments
      }
    });
  };

  const handleAddInstalment = () => {
    if (percentage) {
      const newInstalment = {
        percentage: parseFloat(percentage.toString()),
        method
      };
      updateContractTemplate({
        payment: {
          ...contractTemplate.payment,
          instalments: [...contractTemplate?.payment?.instalments || [], newInstalment]
        }
      });
      setPercentage(0);
    }
  };

  return (
    <Card>
      <>
        <Subtitle text={t('contract_template.payment_method.title')} />
        
        <TinyText text={t('contract_template.payment_method.other_specifications.hint')} />
        <ListContainer
          elements={contractTemplate?.payment?.instalments || []}
          toElementFn={(instalment: Instalment, index: number) =>
            <ListElement key={index}>
              <ElementActions actions={
                <IconButton onClick={() => handleRemoveInstalment(index)}>
                  <Icon url={deleteIcon} cssStyles="w-5" />
                </IconButton>
              }>
                <span>{index + 1}. {instalment.percentage}% - {t(paymentMethodTypeToTranslationKey(instalment.method as PaymentMethodType))}</span>
              </ElementActions>
            </ListElement>
          }
        />
        <div className="flex space-x-3">
          <InputNumber
            defaultValue={percentage}
            validationFn={(value) => !!value && value >= 0} 
            label={t("contract_template.payment_method.percentage_label")!}
            onChange={(e) => setPercentage(Number(e.target.value))}
          />
          <SelectInput
            values={[
              { value: "CARD", readableValue: t(paymentMethodTypeToTranslationKey("CARD")) },
              { value: "CASH", readableValue: t(paymentMethodTypeToTranslationKey("CASH")) },
              { value: "TRANSFER", readableValue: t(paymentMethodTypeToTranslationKey("TRANSFER")) }
            ]}
            onChange={(e: any) => setMethod(e.currentTarget.value)}
            label={t("contract_template.payment_method.method_label")!}
          />
        </div>
        <PrimaryButton fn={handleAddInstalment} text={t("contract_template.payment_method.add_button")!}/>
        <br/>
        <TextArea
          validationFn={value => !!value && isValidText(value, 5)}
          defaultValue={contractTemplate?.payment?.paymentSpecifications}
          label={t('contract_template.payment_method.other_specifications')!}
          onChange={(c) =>
            updateContractTemplate({
              payment: { ...contractTemplate.payment, paymentSpecifications: c.target.value },
            })
          }
        />
        <TinyText text={t('contract_template.payment_method.other_specifications.explainer')} />
      </>
    </Card>
  );
}

